import { consentForCategory, type CookieCategory } from "./cookie-yes"

export const cookieMap: Record<string, CookieCategory[]> = {
  videoIsPaused: ["functional"],
  "habito/mortgage-deals-to-compare": ["functional"],
  "habito/resi-calc-state-v3": ["functional"],
} as const

export type CookieName = keyof typeof cookieMap

const cookieNames = Object.keys(cookieMap) as readonly CookieName[]

const getCookieCategories = (cookie: CookieName): readonly CookieCategory[] =>
  cookieMap[cookie]

export const setLocalStorageIfConsentGranted = (
  key: CookieName,
  value: string
) => {
  if (typeof localStorage !== "undefined" && isCookieConsentGranted(key)) {
    localStorage.setItem(key, value)
  }
}

export const getLocalStorage = (key: CookieName): string | null => {
  if (typeof localStorage !== "undefined") {
    return localStorage.getItem(key)
  }
  return null
}

const isCookieConsentGranted = (cookie: CookieName): boolean =>
  getCookieCategories(cookie).every(category => consentForCategory(category))

export const cullDisallowedCookies = () => {
  cookieNames.forEach(cookie => {
    if (!isCookieConsentGranted(cookie)) {
      localStorage.removeItem(cookie)
    }
  })
}

export const registerCookieEvents = () => {
  document.addEventListener("cookieyes_consent_update", _eventData => {
    cullDisallowedCookies()
  })
}

const blockWrapper = (wrapper: HTMLElement) => {
  const iframes = wrapper.querySelectorAll<HTMLIFrameElement>("iframe")

  iframes.forEach(iframe => iframe.removeAttribute("src"))

  if (!wrapper.querySelector(".consent-placeholder")) {
    const placeholder = document.createElement("div")
    placeholder.className = "consent-placeholder"
    placeholder.textContent = "This content requires your consent to load."
    wrapper.appendChild(placeholder)
  }
}

const allowWrapper = (wrapper: HTMLElement) => {
  const iframes = wrapper.querySelectorAll<HTMLIFrameElement>("iframe")

  iframes.forEach(iframe => {
    const dataSrc = iframe.getAttribute("data-src")
    if (dataSrc) {
      iframe.src = dataSrc
    }
  })

  const placeholder = wrapper.querySelector(".consent-placeholder")
  if (placeholder) {
    placeholder.remove()
  }
}

const handleConsentForWrapper = (wrapper: HTMLElement) => {
  if (consentForCategory("functional")) {
    allowWrapper(wrapper)
  } else {
    blockWrapper(wrapper)
  }
}

const registerWrapper = (wrapper: HTMLElement) => {
  handleConsentForWrapper(wrapper)

  document.addEventListener("cookieyes_banner_load", _eventData => {
    handleConsentForWrapper(wrapper)
  })

  document.addEventListener("cookieyes_consent_update", _eventData => {
    handleConsentForWrapper(wrapper)
  })
}

/* Consent wrappers are an "easy" way for non-devs to wrap
 * iframes in a labelled 'div' and have the embedded content
 * be compliant, even if CookieYes/GTM fail to load.
 */
export const registerConsentWrappers = () => {
  const wrappers = document.querySelectorAll(".consent-wrapper")
  wrappers.forEach(w => registerWrapper(w as HTMLElement))
}
